import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import {
  AccountCircle,
  Settings,
  History,
  Notifications,
  Help,
  ExitToApp,
  Email,
  Person,
  ArrowBack,
  CalendarMonth,
  Link,
  CreditCard,
  DeleteForever
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AffiliateScreen from './AffiliateScreen';
import ProfilePictureUpload from '../ProfilePictureUpload';

const AccountScreen = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogout = async () => {
    setLoading(true);
    setError('');
    try {
      await logout();
      setLogoutDialogOpen(false);
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to logout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    {
      icon: <Person />,
      primary: 'Profile Information',
      secondary: 'Update your personal details',
      onClick: () => navigate('/profile')
    },
    {
      icon: <CreditCard />,
      primary: 'Manage Subscription',
      secondary: 'View and manage your premium subscription',
      onClick: () => navigate('/subscription')
    },
    {
      icon: <Settings />,
      primary: 'Sportsbook Prefrences',
      secondary: 'Change order of sportsbooks',
      onClick: () => navigate('/settings')
    },
    {
      icon: <Help />,
      primary: 'Help & Support',
      secondary: 'Get help and contact support',
      onClick: () => navigate('/help')
    },
    {
      icon: <Notifications />,
      primary: 'Notifications',
      secondary: 'Manage your notification preferences',
      onClick: () => console.log('Notifications clicked')
    },
    {
      icon: <CalendarMonth />,
      primary: 'Affiliate Program',
      secondary: 'Manage your affiliate account and QR code',
      onClick: () => navigate('/affiliate')
    },
    {
      icon: <DeleteForever />,
      primary: 'Delete Account',
      secondary: 'Permanently delete your account',
      onClick: () => navigate('/delete-account'),
      danger: true
    }
  ];

  return (
    <Box sx={{ py: 2 }}>
      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
        >
          Back
        </Button>
      </Box>

      {/* User Profile Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <ProfilePictureUpload
            size={80}
            sx={{ mb: 2 }}
            onUploadSuccess={() => {
              // Profile picture updated successfully
              console.log('Profile picture updated');
            }}
            onUploadError={(error) => {
              setError('Failed to update profile picture: ' + error.message);
            }}
          />

          <Typography variant="h5" gutterBottom>
            {currentUser?.displayName || 'User'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <Email sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {currentUser?.email}
            </Typography>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Member since {currentUser?.metadata?.creationTime
              ? new Date(currentUser.metadata.creationTime).toLocaleDateString()
              : 'N/A'
            }
          </Typography>
        </CardContent>
      </Card>

      {/* Menu Items */}
      <Card>
        <List sx={{ py: 0 }}>
          {menuItems.map((item, index) => (
            <React.Fragment key={index}>
              <ListItem
                button
                onClick={item.onClick}
                sx={{ 
                  py: 2,
                  ...(item.danger && {
                    '&:hover': {
                      backgroundColor: 'error.light',
                      '& .MuiListItemText-primary': {
                        color: 'error.contrastText'
                      },
                      '& .MuiListItemText-secondary': {
                        color: 'error.contrastText'
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'error.contrastText'
                      }
                    }
                  })
                }}
              >
                <ListItemIcon sx={item.danger ? { color: 'error.main' } : {}}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.primary}
                  secondary={item.secondary}
                  sx={item.danger ? { 
                    '& .MuiListItemText-primary': { color: 'error.main' },
                    '& .MuiListItemText-secondary': { color: 'error.main' }
                  } : {}}
                />
              </ListItem>
              {index < menuItems.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Card>

      {/* Logout Button */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<ExitToApp />}
          onClick={() => setLogoutDialogOpen(true)}
          fullWidth
          sx={{ py: 1.5 }}
        >
          Sign Out
        </Button>
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Sign Out</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography>
            Are you sure you want to sign out of your account?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setLogoutDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogout}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Signing Out...' : 'Sign Out'}
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
};

export default AccountScreen;
